<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <!-- 🌟 HEADER -->
    <header class="bg-[#1a1a1a] text-white shadow sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <div class="flex items-center gap-2">
          <img src="/logo.png" alt="Logo" class="h-10 w-auto" />
          <span class="font-bold text-lg">GetLance</span>
        </div>

        <div class="flex gap-3">
          <router-link
            to="/login"
            class="px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition"
          >
            Fazer login
          </router-link>
        </div>
      </div>
    </header>

    <!-- 🔐 FORMULÁRIO DE CADASTRO -->
    <section class="flex-1 flex items-center justify-center px-6 py-16">
      <div
        class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#e0dcd6]"
      >
        <h2 class="text-3xl font-bold text-[#152a34] mb-1">Criar conta</h2>
        <p class="text-gray-500 mb-8">Leva menos de 1 minuto.</p>

        <!-- NOME -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Nome completo</label>
          <input
            type="text"
            v-model="form.name"
            placeholder="Seu nome"
            class="input-gold"
          />
        </div>

        <!-- EMAIL -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">E-mail</label>
          <input
            type="email"
            v-model="form.email"
            placeholder="seuemail@exemplo.com"
            class="input-gold"
          />
        </div>

        <!-- SENHA -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Senha</label>
          <input
            type="password"
            v-model="form.password"
            placeholder="********"
            class="input-gold"
          />
        </div>

        <!-- BOTÃO CRIAR CONTA -->
        <button
          @click="register"
          class="w-full py-3 rounded-full bg-[#b99a52] hover:bg-[#a18446] text-white font-semibold transition"
          :disabled="isSubmitting"
        >
          <span v-if="!isSubmitting">Criar conta</span>
          <span v-else>Cadastrando...</span>
        </button>

        <!-- DIVISOR -->
        <div class="flex items-center my-6">
          <div class="flex-grow border-t border-gray-300"></div>
          <span class="mx-3 text-gray-500 text-sm">ou</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>

        <!-- GOOGLE LOGIN -->
        <!-- GOOGLE SIGNUP -->
<div class="mb-6">
  <div ref="googleBtn" class="w-full flex justify-center"></div>
</div>

        <!-- LOGIN LINK -->
        <p class="text-center text-gray-600 text-sm mt-6">
          Já tem conta?
          <router-link to="/login" class="text-[#b99a52] font-semibold hover:underline">
            Fazer login
          </router-link>
        </p>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, getCurrentInstance } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import api from "@/services/api";
import AppFooter from "@/components/AppFooter.vue";

const router = useRouter();
const route = useRoute();
const store = useStore();
const { proxy } = getCurrentInstance();

const GOOGLE_CLIENT_ID =
  "708117913743-j1tmpf9b8endvjd3im363cniugltsvjd.apps.googleusercontent.com";

const googleBtn = ref(null);

const form = ref({
  name: "",
  email: "",
  password: "",
});

const isSubmitting = ref(false);
const validationErrors = ref({});

function validateForm() {
  const errors = {};
  const data = form.value;

  if (!data.name.trim()) errors.name = "O nome é obrigatório.";
  if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
    errors.email = "E-mail inválido ou obrigatório.";
  if (!data.password) errors.password = "A senha é obrigatória.";
  else if (data.password.length < 6)
    errors.password = "A senha deve ter no mínimo 6 caracteres.";

  validationErrors.value = errors;
  return Object.keys(errors).length === 0;
}

async function register() {
  if (!validateForm()) {
    Swal.fire({
      icon: "warning",
      title: "Corrija os campos",
      text: "Há informações inválidas ou faltando.",
    });
    return;
  }

  isSubmitting.value = true;

  try {
    await api.post("/register", {
      name: form.value.name,
      email: form.value.email,
      password: form.value.password,
      role: "user",
    });

    Swal.fire({
      icon: "success",
      title: "Cadastro realizado!",
      text: "Verifique seu e-mail para confirmar seu cadastro.",
    });

    setTimeout(() => router.push("/login"), 1800);
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Usuário não ativado",
        text: "Verifique seu e-mail para ativar sua conta.",
      });
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Erro ao cadastrar",
      text: error?.response?.data?.error || "Tente novamente mais tarde.",
    });
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  await nextTick();

  try {
    await waitForGoogle(5000); // aguarda até 5s
  } catch {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Google Sign-In não carregou a tempo. Recarregue a página.",
    });
    return;
  }

  if (!googleBtn.value) {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Container do botão Google não foi encontrado (ref googleBtn).",
    });
    return;
  }

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleGoogleCallback,
  });

  window.google.accounts.id.renderButton(googleBtn.value, {
    theme: "outline",
    size: "large",
    text: "signup_with",
    shape: "pill",
    width: 360,
    logo_alignment: "left",
  });
});

const waitForGoogle = (timeoutMs = 5000) => {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const tick = () => {
      if (window.google?.accounts?.id) return resolve(true);
      if (Date.now() - start >= timeoutMs) return reject(new Error("timeout"));
      setTimeout(tick, 50);
    };

    tick();
  });
};

const handleGoogleCallback = async (response) => {
  try {
    isSubmitting.value = true;

    const result = await store.dispatch("signInWithGoogle", {
      credential: response.credential,
    });

    if (result?.token) {
      router.push("/");
      return;
    }

    if (result?.success) {
      Swal.fire({
        icon: "success",
        title: "Cadastro realizado!",
        text: "Verifique seu e-mail para confirmar seu cadastro.",
      });

      setTimeout(() => router.push("/login"), 1800);
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Erro ao entrar com Google.",
      text: "Resposta inválida do servidor. Tente novamente.",
    });

  } catch (error) {
    console.error("Erro ao autenticar com Google:", error);
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Usuário não ativado",
        text: "Verifique seu e-mail para ativar sua conta.",
      });
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Erro ao entrar com Google.",
      text: error.response?.data?.error || "Tente novamente mais tarde.",
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>

</style>
